import { DialogHeader, DialogFooter } from "@/components/ui/dialog";
import { TimeSheetDetails } from "@/v2/api/time-sheet/FindOneTimeSheetLog";
import SaveTimeSheet from "@/v2/api/time-sheet/SaveTimeSheet";
import {
    Dialog,
    DialogTrigger,
    DialogContent,
    DialogTitle,
    DialogDescription,
    DialogClose,
} from "@/components/ui/dialog";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Loader2 } from "lucide-react";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { ArForm, ArTextAreaFormField } from "@/v2/components/ui/form";
import { SaveTimeSheetSchema } from "@/v2/schema/time-sheet";
import { toast } from "sonner";

const CommentForm = ({
    timeSheetDetails,
    timeSheetId,
    closeOnSave,
}: {
    timeSheetId: string;
    timeSheetDetails: TimeSheetDetails;
    closeOnSave: (value: boolean) => void;
}) => {
    const queryClient = useQueryClient();

    const form = useForm<SaveTimeSheetSchema>({
        resolver: zodResolver(SaveTimeSheetSchema),
        defaultValues: {
            title: timeSheetDetails?.title,
            deduction_note: timeSheetDetails?.deduction_note,
            description: timeSheetDetails?.description,
            other_deductions: timeSheetDetails?.other_deductions,
            status: timeSheetDetails?.status,
            note: timeSheetDetails.note,
        },
    });

    const CreateMutation = useMutation({
        mutationKey: ["time-sheets-logs", timeSheetId],
        mutationFn: (data: SaveTimeSheetSchema) =>
            SaveTimeSheet({ timeSheetId: timeSheetId, data }),
    });

    const handleFormSubmit = (formData: SaveTimeSheetSchema) => {
        const payload = { ...timeSheetDetails, note: formData.note };
        CreateMutation.mutate(payload, {
            onError: (err) => {
                console.error({ err });
                toast.error("Oops! Something went wrong", {
                    description: `While closing the month, we encountered an issue.`,
                });
            },
            onSuccess: () => {
                queryClient.invalidateQueries({
                    queryKey: ["time-sheets-logs", timeSheetId],
                });
                toast.success(`Comment Added successfully`);
                closeOnSave(false);
            },
        });
    };

    return (
        <ArForm
            onSubmit={handleFormSubmit}
            form={form}
            formSchema={SaveTimeSheetSchema}
        >
            <ArTextAreaFormField<SaveTimeSheetSchema>
                label={"Comment"}
                name={"note"}
            />
            <DialogFooter className="mt-5">
                <Button
                    type="submit"
                    disabled={CreateMutation.isPending}
                    className="w-28"
                >
                    {CreateMutation.isPending ? <Loader2 /> : "Save Changes"}
                </Button>
                <DialogClose asChild>
                    <Button variant="outline">Cancel</Button>
                </DialogClose>
            </DialogFooter>
        </ArForm>
    );
};

export default function AddCommentDialog({
    timeSheetId,
    timeSheetDetails,
}: {
    timeSheetId: string;
    timeSheetDetails: TimeSheetDetails;
}) {
    const [open, setOpen] = useState(false);

    return (
        <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
                <Button variant="outline">Add Comment</Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-sm">
                <DialogHeader>
                    <DialogTitle>Add a Comment</DialogTitle>
                    <DialogDescription>
                        Add any additional comments if needed.
                    </DialogDescription>
                </DialogHeader>
                <CommentForm
                    timeSheetDetails={timeSheetDetails}
                    timeSheetId={timeSheetId}
                    closeOnSave={setOpen}
                />
            </DialogContent>
        </Dialog>
    );
}
